// AI TricoAcademy™ — Diagnosi Base
// Libreria formativa base per parrucchieri. Contenuto rivedibile/traducibile
// senza toccare il componente React.
//
// Struttura di ogni categoria:
//   id, title, accent (icon name in lucide-react), subtitle,
//   description, cosa_osservare[], errori_comuni[], impatto_diagnosi,
//   indicazioni_operative[].

export const ACADEMY_CATEGORIES = [
  {
    id: "cute-sana",
    title: "Cute sana",
    accent: "Heart",
    tone: "emerald",
    subtitle: "Il riferimento visivo di una cute in equilibrio",
    description:
      "Una cute sana è il punto di partenza per riconoscere tutte le altre condizioni. " +
      "Presenta un colore uniforme, una superficie liscia, osti follicolari liberi e " +
      "una distribuzione regolare del capello. Allenarsi a riconoscerla permette di " +
      "individuare subito qualunque anomalia.",
    cosa_osservare: [
      "colore della cute uniforme (rosato chiaro o leggermente ambrato)",
      "osti follicolari visibili e liberi",
      "assenza di squamette, rossore o accumuli",
      "capelli in uscita regolari, di calibro omogeneo",
    ],
    errori_comuni: [
      "scambiare un leggero riflesso della pelle per “cute grassa”",
      "non fotografare in modo sufficientemente ravvicinato",
    ],
    impatto_diagnosi:
      "Avere in mente l’immagine di riferimento di una cute sana aiuta a classificare " +
      "correttamente le deviazioni (secca, grassa, desquamativa, ecc.).",
    indicazioni_operative: [
      "mantenere la routine domiciliare di manutenzione",
      "riprogrammare un controllo ogni 2-3 mesi",
    ],
  },
  {
    id: "eccesso-sebo",
    title: "Eccesso di sebo",
    accent: "Droplet",
    tone: "amber",
    subtitle: "Quando la cute produce più sebo del normale",
    description:
      "La cute appare più lucida del normale, con accumuli oleosi alla base del " +
      "fusto. I capelli si sporcano più rapidamente e tendono ad appiattirsi già " +
      "in giornata. Spesso si accompagna a ostruzione ostiale.",
    cosa_osservare: [
      "superficie cutanea più lucida e untuosa",
      "accumuli alla base dei capelli, soprattutto nella zona frontale e del vertice",
      "eventuale comparsa di sebo ossidato (aspetto giallo-brunastro)",
    ],
    errori_comuni: [
      "confondere l’eccesso di sebo con forfora grassa (sono due cose diverse)",
      "limitarsi a consigliare uno shampoo aggressivo — serve riequilibrio, non solo detersione",
    ],
    impatto_diagnosi:
      "La gestione corretta parte dal riequilibrio sebaceo, non dalla sola pulizia " +
      "profonda. Importante anche valutare se c’è componente desquamativa associata.",
    indicazioni_operative: [
      "detersione con shampoo sebo-equilibrante, 2-3 volte a settimana",
      "lozione riequilibrante nei giorni alternati",
      "ciclo di trattamenti purificanti in salone",
    ],
  },
  {
    id: "cute-secca",
    title: "Cute secca",
    accent: "Wind",
    tone: "sky",
    subtitle: "Quando manca idratazione a livello della cute",
    description:
      "La cute appare opaca, tirante, con possibili piccole squamette fini. Il " +
      "cliente può riferire sensazione di tensione dopo lo shampoo. La cute secca " +
      "è diversa dalla forfora secca: qui il problema è idratazione, non turnover.",
    cosa_osservare: [
      "cute di colore più pallido, leggermente opaca",
      "piccole squamette sottili, ma poco aderenti",
      "aspetto più “tirato” rispetto a una cute sana",
    ],
    errori_comuni: [
      "trattarla come forfora secca senza distinguere i due casi",
      "usare shampoo troppo aggressivi che peggiorano la secchezza",
    ],
    impatto_diagnosi:
      "Richiede idratazione mirata, non cheratolisi. Sbagliare approccio può " +
      "accentuare il problema.",
    indicazioni_operative: [
      "shampoo idratante delicato",
      "lozione o siero idratante ad uso regolare",
      "evitare detergenti aggressivi e acqua troppo calda",
    ],
  },
  {
    id: "forfora-secca",
    title: "Forfora secca",
    accent: "Snowflake",
    tone: "slate",
    subtitle: "Squamette fini e leggere, facili da staccare",
    description:
      "Piccole squamette biancastre, secche, che si distaccano facilmente e " +
      "possono cadere su spalle e vestiti. Diversa dalla cute secca perché qui " +
      "c’è alterazione del turnover cellulare.",
    cosa_osservare: [
      "squame fini, friabili, di colore bianco-grigiastro",
      "distribuzione spesso diffusa sul cuoio capelluto",
      "assenza di sebo in eccesso",
    ],
    errori_comuni: [
      "trattarla come forfora grassa, usando prodotti sgrassanti inutili",
      "non associare un’azione lenitiva quando la cute è anche sensibile",
    ],
    impatto_diagnosi:
      "Il focus deve essere normalizzare il turnover e lenire la cute; non serve " +
      "un’azione sgrassante forte.",
    indicazioni_operative: [
      "shampoo antiforfora specifico per forfora secca",
      "trattamento lenitivo a supporto",
      "ciclo di 3 trattamenti di salone a distanza ravvicinata",
    ],
  },
  {
    id: "forfora-grassa",
    title: "Forfora grassa",
    accent: "Droplets",
    tone: "orange",
    subtitle: "Squame più grandi, gialle, oleose e aderenti",
    description:
      "Squame più grandi delle forfora secca, di aspetto giallastro, oleose e " +
      "aderenti alla cute. Convive quasi sempre con una componente di eccesso di " +
      "sebo e può dare prurito.",
    cosa_osservare: [
      "squame giallastre, untuose, aderenti",
      "cute spesso lucida, con accumuli di sebo",
      "possibile leggero arrossamento localizzato",
    ],
    errori_comuni: [
      "confonderla con il semplice eccesso di sebo",
      "usare un antiforfora secco aggressivo senza azione sebo-equilibrante",
    ],
    impatto_diagnosi:
      "Richiede un approccio misto: riequilibrio sebaceo + azione antiforfora " +
      "specifica. Spesso accompagnato da lieve irritabilità da lenire.",
    indicazioni_operative: [
      "shampoo sebo-equilibrante + antiforfora in alternanza",
      "lozione riequilibrante a supporto",
      "ciclo mirato in salone con azione purificante",
    ],
  },
  {
    id: "desquamazione",
    title: "Desquamazione",
    accent: "Layers",
    tone: "stone",
    subtitle: "Accumulo di cellule morte in superficie",
    description:
      "Si presenta come squamette (fini, medie o compatte) sulla superficie " +
      "della cute. Può essere una condizione a sé o associarsi a forfora, sebo " +
      "in eccesso o cute secca.",
    cosa_osservare: [
      "squamette aderenti (più tenaci) o libere (più mobili)",
      "distribuzione localizzata o diffusa",
      "eventuale presenza di accumulo alla base del fusto",
    ],
    errori_comuni: [
      "usare sempre un antiforfora indipendentemente dal tipo di squama",
      "non riconoscere la variante aderente, che richiede esfoliazione specifica",
    ],
    impatto_diagnosi:
      "La priorità è distinguere desquamazione libera da aderente — il protocollo " +
      "cambia in modo sostanziale.",
    indicazioni_operative: [
      "scrub esfoliante pre-shampoo per le forme aderenti",
      "shampoo con azione normalizzante",
      "trattamento di salone che liberi gli osti follicolari",
    ],
  },
  {
    id: "rossore-sensibilita",
    title: "Arrossamento / sensibilità",
    accent: "Flame",
    tone: "rose",
    subtitle: "Cute reattiva che richiede azione lenitiva",
    description:
      "La cute presenta zone di arrossamento, talvolta localizzato attorno agli " +
      "osti. Il cliente riferisce sensazione di bruciore o fastidio con certi " +
      "prodotti. Richiede prodotti lenitivi e una detersione delicata.",
    cosa_osservare: [
      "aree rosate o arrossate, in particolare alla base del fusto",
      "cute che reagisce anche a tocchi leggeri",
      "possibile sensazione di tensione riferita dal cliente",
    ],
    errori_comuni: [
      "applicare prodotti antiforfora senza componente lenitiva",
      "usare spazzole aggressive durante il lavaggio",
    ],
    impatto_diagnosi:
      "La componente lenitiva deve essere SEMPRE presente nel protocollo, anche " +
      "quando c’è un’altra problematica dominante.",
    indicazioni_operative: [
      "shampoo lenitivo senza solfati aggressivi",
      "lozione lenitiva con attivi calmanti",
      "evitare temperatura alta dell’acqua e stress meccanico",
    ],
  },
  {
    id: "ispessimento-cutaneo",
    title: "Ispessimento cutaneo",
    accent: "Layers3",
    tone: "zinc",
    subtitle: "Cute più spessa, con ridotta ossigenazione",
    description:
      "La cute appare più spessa, opaca, con osti poco visibili. Spesso è il " +
      "risultato di anni di accumulo non trattato. Rende difficile la piena " +
      "uscita del capello e va gestita con un lavoro di normalizzazione " +
      "graduale.",
    cosa_osservare: [
      "superficie cutanea più spessa e “corposa”",
      "osti follicolari meno evidenti o parzialmente chiusi",
      "minore elasticità visibile della cute",
    ],
    errori_comuni: [
      "scambiare l’ispessimento per semplice accumulo temporaneo",
      "voler risolvere tutto in un solo trattamento",
    ],
    impatto_diagnosi:
      "Serve un piano di normalizzazione a medio termine (più trattamenti), non " +
      "un singolo intervento spot.",
    indicazioni_operative: [
      "scrub/esfoliante a casa una volta a settimana",
      "ciclo di 3-5 trattamenti professionali in salone",
      "monitoraggio con foto di controllo ogni 30 giorni",
    ],
  },
  {
    id: "pori-ostruiti",
    title: "Pori ostruiti",
    accent: "Circle",
    tone: "amber",
    subtitle: "Osti follicolari parzialmente chiusi",
    description:
      "Osti follicolari occupati da accumulo di sebo e cellule morte. Il " +
      "capello può emergere con difficoltà. Uno degli indicatori più " +
      "importanti per il parrucchiere perché incide sulla qualità del " +
      "capello in uscita.",
    cosa_osservare: [
      "osti non più liberi, con materiale al centro",
      "alone biancastro/giallastro attorno al foro del capello",
      "capelli che escono più sottili o curvi",
    ],
    errori_comuni: [
      "non controllare mai l’ostio, fermandosi solo al capello",
      "usare prodotti coprenti che peggiorano l’occlusione",
    ],
    impatto_diagnosi:
      "Se l’ostio non si libera, nessun trattamento sulle lunghezze darà " +
      "risultati duraturi. La cute è la base.",
    indicazioni_operative: [
      "scrub esfoliante pre-shampoo, 1-2 volte a settimana",
      "shampoo purificante normalizzante",
      "trattamento in salone con azione di liberazione ostiale",
    ],
  },
  {
    id: "bulbi-indeboliti",
    title: "Bulbi indeboliti",
    accent: "TrendingDown",
    tone: "violet",
    subtitle: "Capelli più sottili, densità ridotta",
    description:
      "Il capello in uscita appare più sottile rispetto alla norma, possono " +
      "comparire zone con densità ridotta. È un segnale precoce da non " +
      "ignorare: un intervento tempestivo può fare una grande differenza.",
    cosa_osservare: [
      "capelli in uscita di calibro più piccolo",
      "zone con meno capelli per unità follicolare",
      "cute più visibile in controluce",
    ],
    errori_comuni: [
      "ignorare i segnali iniziali aspettando un problema evidente",
      "usare linguaggio medico con il cliente (“calvizie”, “patologia”)",
    ],
    impatto_diagnosi:
      "La strategia deve includere sempre una componente di stimolazione e " +
      "supporto della densità. Anche nelle fasi iniziali.",
    indicazioni_operative: [
      "shampoo energizzante con attivi stimolanti",
      "lozione o fiala anticaduta in uso continuativo",
      "ciclo di trattamenti microcircolo/rinforzo follicolare in salone",
    ],
  },
];

// Struttura predisposta per upgrade futuri (non visibili oggi).
export const ACADEMY_FUTURE_MODULES = [
  {
    id: "quiz",
    title: "Quiz formativi",
    description: "Test di autovalutazione per consolidare la lettura visiva della cute.",
  },
  {
    id: "certificazione",
    title: "Certificazione operatore",
    description: "Percorso di certificazione base AI TricoProtocol™ con attestato finale.",
  },
  {
    id: "livelli-avanzati",
    title: "Livelli avanzati",
    description: "Moduli su miniaturizzazione, irritazione, dermatiti cosmetiche e refertazione avanzata.",
  },
];
